<template>
	<!-- 容器必须设置宽高，否则图表无法显示 -->
	<div ref="chartRef" class="echarts-container" :style="{ width: width, height: height }"></div>
</template>

<script>
import * as echarts from 'echarts'

export default {
	name: 'Echarts',
	props: {
		// 图表类型：line(折线图) / pie(饼图) / bar(排位表/柱状图)
		type: {
			type: String,
			required: true,
			validator: (val) => ['line', 'pie', 'bar'].includes(val),
		},
		// 图表配置项（会覆盖默认配置）
		options: {
			type: Object,
			default: () => ({}),
		},
		// 图表宽度
		width: {
			type: String,
			default: '100%',
		},
		// 图表高度
		height: {
			type: String,
			default: '400px',
		},
		// 是否自适应窗口大小
		autoResize: {
			type: Boolean,
			default: true,
		},
	},
	data() {
		return {
			chartInstance: null, // ECharts 实例
		}
	},
	watch: {
		// 监听配置项变化，更新图表
		options: {
			deep: true,
			handler() {
				this.updateChart()
			},
		},
		// 监听类型变化，重新初始化图表
		type() {
			this.initChart()
		},
	},
	mounted() {
		// 初始化图表
		this.initChart()
		// 绑定窗口大小变化事件
		if (this.autoResize) {
			window.addEventListener('resize', this.handleResize)
		}
	},
	beforeDestroy() {
		// 销毁实例和事件监听
		if (this.chartInstance) {
			this.chartInstance.dispose()
			this.chartInstance = null
		}
		if (this.autoResize) {
			window.removeEventListener('resize', this.handleResize)
		}
	},
	methods: {
		// 初始化图表
		initChart() {
			// 确保容器存在
			if (!this.$refs.chartRef) return

			// 初始化实例
			this.chartInstance = echarts.init(this.$refs.chartRef)

			// 合并默认配置和传入配置
			const finalOptions = this.mergeDefaultOptions()

			// 渲染图表
			this.chartInstance.setOption(finalOptions)
		},

		// 合并默认配置
		mergeDefaultOptions() {
			// 基础配置
			const baseOptions = {
				tooltip: {
					trigger: 'axis',
					textStyle: { fontSize: 12 },
				},
				legend: {
					top: 'top',
					textStyle: { fontSize: 12 },
				},
				grid: {
					left: '3%',
					right: '4%',
					bottom: '3%',
					containLabel: true,
				},
			}

			// 不同类型的默认配置
			const typeDefaultOptions = {
				// 折线图默认配置
				line: {
					xAxis: {
						type: 'category',
						boundaryGap: false,
					},
					yAxis: {
						type: 'value',
					},
					series: [],
				},
				// 饼图默认配置
				pie: {
					tooltip: {
						trigger: 'item',
					},
					series: [
						{
							type: 'pie',
							radius: ['40%', '70%'], // 内环外环半径
							center: ['50%', '50%'], // 居中
							label: {
								show: true,
								formatter: '{b}: {c} ({d}%)', // 显示名称、数值、百分比
							},
						},
					],
				},
				// 排位表（柱状图）默认配置
				bar: {
					xAxis: {
						type: 'category',
					},
					yAxis: {
						type: 'value',
					},
					series: [
						{
							type: 'bar',
							itemStyle: {
								borderRadius: [4, 4, 0, 0], // 顶部圆角
							},
						},
					],
				},
			}

			// 步骤1：先合并基础配置和类型默认配置（得到基础完整配置）
			const baseCompleteOptions = {
				...baseOptions,
				...typeDefaultOptions[this.type],
			}

			// 步骤2：单独处理 series 配置（核心修复：保证 type 不丢失，合并其他属性）
			const finalSeries = []
			const defaultSeries = baseCompleteOptions.series || []
			const incomingSeries = this.options.series || []

			// 优先使用外部传入的series数量，不足时用默认补充
			const seriesLength = Math.max(defaultSeries.length, incomingSeries.length)

			for (let index = 0; index < seriesLength; index++) {
				const defaultItem = defaultSeries[index] || { type: this.type } // 兜底默认type
				const incomingItem = incomingSeries[index] || {}

				const mergedSeriesItem = {
					...defaultItem,
					...incomingItem,
				}

				mergedSeriesItem.type = defaultItem.type // 强制保留type
				finalSeries.push(mergedSeriesItem)
			}

			// 步骤3：合并最终配置（排除外部传入的 series，使用上面合并后的 finalSeries）
			const { series: _, ...incomingOptionsWithoutSeries } = this.options
			const finalOptions = {
				...baseCompleteOptions,
				...incomingOptionsWithoutSeries,
				series: finalSeries,
			}

			return finalOptions
		},

		// 更新图表
		updateChart() {
			if (!this.chartInstance) return
			const finalOptions = this.mergeDefaultOptions()
			// 第二个参数改为 false（默认值），不强制清空所有配置再重绘，仅更新差异
			this.chartInstance.setOption(finalOptions, false)
		},

		// 窗口大小变化时自适应
		handleResize() {
			if (this.chartInstance) {
				this.chartInstance.resize()
			}
		},
	},
}
</script>

<style scoped>
.echarts-container {
	transition: all 0.3s ease;
}
</style>



// WEBPACK FOOTER //
// echarts.vue?0ea2dea2